import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";

import { curriculumService, type Curriculum, type Subject, type SubjectInput, type Syllabus } from "../../services/curriculumService";
import { apiErrorMessage } from "../../utils/apiError";
import { ConfirmDialog, type ConfirmRequest } from "../ConfirmDialog";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import {
  CURRICULUM_TYPES,
  activeStatus,
  categoryLabel,
  curriculumConfig,
  labelFrom,
  levelLabel,
  percent,
  subjectConfig,
  type CurriculumRow,
  type SubjectRow,
} from "./academicsLogic";
import { CurriculumForm, SubjectForm } from "./CurriculumForms";
import { Notice, RecordFooter, UsageNotice, deleteErrorText, type NoticeState } from "../register/RecordParts";

const QUERY_KEYS = ["academics-subjects", "academics-curricula", "academics-record", "subjects", "curricula"];

/** Save, make (in)active and delete for one kind of record, with the notice
 * each leaves behind. */
function useRecordMutations<T extends { id?: number }>({
  noun,
  save,
  setActive,
  remove,
  onSaved,
  onDeleted,
  setNotice,
}: {
  noun: string;
  save: (data: T) => Promise<unknown>;
  setActive: (id: number, isActive: boolean) => Promise<unknown>;
  remove: (id: number) => Promise<unknown>;
  onSaved: () => void;
  onDeleted: () => void;
  setNotice: (notice: NoticeState) => void;
}) {
  const queryClient = useQueryClient();
  const refresh = () => Promise.all(QUERY_KEYS.map((key) => queryClient.invalidateQueries({ queryKey: [key] })));
  return {
    save: useMutation({
      mutationFn: save,
      onSuccess: (_result, variables) => {
        onSaved();
        setNotice({ tone: "success", text: `${noun} ${variables.id ? "saved" : "created"}.` });
        refresh();
      },
    }),
    toggle: useMutation({
      mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) => setActive(id, isActive),
      onSuccess: (_result, variables) => {
        setNotice({
          tone: "success",
          text: variables.isActive ? `${noun} made active.` : `${noun} made inactive. It stays on record and can be made active again.`,
        });
        refresh();
      },
      onError: (error) => setNotice({ tone: "error", text: apiErrorMessage(error, `The ${noun.toLowerCase()} could not be changed.`) }),
    }),
    remove: useMutation({
      mutationFn: remove,
      onSuccess: () => {
        onDeleted();
        setNotice({ tone: "success", text: `${noun} deleted.` });
        refresh();
      },
      onError: (error) => {
        onDeleted();
        setNotice({ tone: "error", text: deleteErrorText(error) });
      },
    }),
  };
}

// --- Subjects ------------------------------------------------------------------------

const subjectColumns: RegisterColumn<SubjectRow>[] = [
  { header: "Subject", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name, className: "min-w-[11rem] whitespace-normal" },
  { header: "Code", cell: (row) => row.code, exportValue: (row) => row.code, className: "font-mono text-xs text-gray-600" },
  {
    header: "Curriculum",
    cell: (row) => <span title={row.curriculum_name} className="block max-w-[13rem] truncate">{row.curriculum_name}</span>,
    exportValue: (row) => row.curriculum_name,
    className: "text-gray-700",
  },
  { header: "Category", cell: (row) => categoryLabel(row.category), exportValue: (row) => categoryLabel(row.category), className: "text-gray-700" },
  // Compulsory or optional is a view above rather than a column, so the sheet fits; exports keep it.
  { header: "Taken by", cell: () => null, exportValue: (row) => (row.is_compulsory ? "Compulsory" : "Optional"), exportOnly: true },
  { header: "Periods / week", cell: (row) => row.weekly_periods, exportValue: (row) => row.weekly_periods, align: "right" },
  { header: "Pass mark", cell: (row) => percent(row.passing_grade), exportValue: (row) => Number(row.passing_grade), align: "right" },
  {
    header: "Syllabi",
    cell: (row) => (row.syllabusCount ? row.syllabusCount : row.is_active ? <span className="text-amber-700">None</span> : <span className="text-gray-300">0</span>),
    exportValue: (row) => row.syllabusCount,
    align: "right",
  },
  { header: "Status", cell: (row) => <StatusDot {...activeStatus(row)} />, exportValue: (row) => activeStatus(row).label },
];

export function SubjectsPanel({ rows, curricula, syllabi, isLoading, isError, onRetry, canWrite, openId }: {
  /** A subject to open straight away, e.g. one picked from a curriculum. */
  openId?: number | null;
  rows?: SubjectRow[];
  curricula: Curriculum[];
  syllabi: Syllabus[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canWrite: boolean;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(openId ?? null);
  const [editing, setEditing] = useState<{ record?: Subject } | null>(null);
  const [dialog, setDialog] = useState<ConfirmRequest | null>(null);
  const usage = useQuery({
    queryKey: ["academics-record", "subject", viewing],
    queryFn: () => curriculumService.getSubject(viewing!),
    enabled: viewing !== null && canWrite,
  });
  const actions = useRecordMutations<SubjectInput & { id?: number }>({
    noun: "Subject",
    save: ({ id, ...data }) => (id ? curriculumService.updateSubject(id, data) : curriculumService.createSubject(data)),
    setActive: (id, isActive) => curriculumService.updateSubject(id, { is_active: isActive }),
    remove: curriculumService.deleteSubject,
    onSaved: () => setEditing(null),
    onDeleted: () => {
      setDialog(null);
      setViewing(null);
    },
    setNotice,
  });

  const subject = viewing !== null && !editing && !dialog ? rows?.find((row) => row.id === viewing) : undefined;
  const ownSyllabi = subject ? syllabi.filter((syllabus) => syllabus.subject === subject.id) : [];
  const busy = actions.toggle.isPending || actions.remove.isPending;
  const hasCurriculum = curricula.some((item) => item.is_active);

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Subjects"
        description={hasCurriculum || !canWrite ? "Open a subject to see its syllabi and materials, or to change it." : "Create a curriculum first; every subject belongs to one."}
        actions={canWrite && (
          <button
            type="button"
            disabled={!hasCurriculum}
            title={hasCurriculum ? undefined : "Create a curriculum first"}
            onClick={() => { actions.save.reset(); setEditing({}); }}
            className="btn-primary h-8 min-h-8 px-3 text-xs disabled:opacity-50"
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" /> New subject
          </button>
        )}
        rows={rows}
        config={subjectConfig}
        columns={subjectColumns}
        rowKey={(row) => row.id}
        onOpen={(row) => setViewing(row.id)}
        noun={["subject", "subjects"]}
        exportName={{ file: "subjects", title: "Subjects" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canWrite ? "No subjects yet. Add the first one." : "No subjects have been set up yet."}
      />

      {subject && (
        <DetailsModal
          title={subject.name}
          headline={{ label: "Periods a week", value: String(subject.weekly_periods), note: `${subject.code} · ${subject.curriculum_name}` }}
          status={activeStatus(subject)}
          facts={[
            ["Code", <span className="font-mono">{subject.code}</span>],
            ["Curriculum", subject.curriculum_name],
            ["Category", categoryLabel(subject.category)],
            ["Taken by", subject.is_compulsory ? "Every learner (compulsory)" : "Learners who choose it"],
            ["Examinable", subject.is_examinable ? "Yes" : "No"],
            ["Weight in overall result", percent(subject.assessment_weight)],
            ["Pass mark", percent(subject.passing_grade)],
            ["Prerequisites", subject.prerequisite_names?.join(", ") || "None"],
          ]}
          notice={<UsageNotice inUse={usage.data?.in_use} advice="Make it inactive to stop it being offered for new syllabi, timetables and exams." />}
          sections={[
            { title: "Description", body: subject.description },
            { title: "Textbooks", body: subject.textbooks },
            { title: "Reference materials", body: subject.reference_materials },
            { title: "Equipment needed", body: subject.equipment_required },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <RecordFooter
              canWrite={canWrite}
              isActive={subject.is_active}
              inUse={usage.data?.in_use}
              busy={busy}
              onClose={() => setViewing(null)}
              onEdit={() => { actions.save.reset(); setEditing({ record: subject }); }}
              onToggle={() => actions.toggle.mutate({ id: subject.id, isActive: !subject.is_active })}
              onDelete={() =>
                setDialog({
                  title: "Delete subject?",
                  body: <p><strong>{subject.name}</strong> will be permanently removed. Nothing uses it yet, and this cannot be undone.</p>,
                  confirmLabel: "Delete subject",
                  tone: "danger",
                  onConfirm: () => actions.remove.mutate(subject.id),
                })
              }
            />
          }
        >
          <section>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Syllabi for this subject</h4>
            {ownSyllabi.length ? (
              <ol className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                {ownSyllabi.map((syllabus) => (
                  <li key={syllabus.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-sm">
                    <span className="font-medium text-gray-900">{syllabus.title}</span>
                    <span className="text-xs text-gray-500">{syllabus.grade_name} · version {syllabus.version}</span>
                    <StatusDot {...activeStatus(syllabus)} />
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-sm text-gray-500">No syllabus yet. Teachers plan against one, so add it in Syllabus Management.</p>
            )}
          </section>
        </DetailsModal>
      )}

      {editing && (
        <SubjectForm
          record={editing.record}
          curricula={curricula}
          busy={actions.save.isPending}
          error={actions.save.isError ? apiErrorMessage(actions.save.error, "The subject could not be saved.") : undefined}
          onSave={(data) => actions.save.mutate({ ...data, id: editing.record?.id })}
          onClose={() => setEditing(null)}
        />
      )}
      <ConfirmDialog request={dialog} onClose={() => setDialog(null)} busy={actions.remove.isPending} />
    </div>
  );
}

// --- Curricula ----------------------------------------------------------------------

const curriculumColumns: RegisterColumn<CurriculumRow>[] = [
  { header: "Curriculum", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name },
  { header: "Type", cell: (row) => labelFrom(CURRICULUM_TYPES, row.curriculum_type), exportValue: (row) => labelFrom(CURRICULUM_TYPES, row.curriculum_type), className: "text-gray-700" },
  { header: "Level", cell: (row) => levelLabel(row.education_level), exportValue: (row) => levelLabel(row.education_level), className: "text-gray-700" },
  { header: "Version", cell: (row) => row.version, exportValue: (row) => row.version, className: "text-gray-700" },
  { header: "In use from", cell: (row) => row.start_date, exportValue: (row) => row.start_date, className: "tabular-nums text-gray-700" },
  { header: "Subjects", cell: (row) => row.subjectCount, exportValue: (row) => row.subjectCount, align: "right" },
  { header: "Syllabi", cell: (row) => row.syllabusCount, exportValue: (row) => row.syllabusCount, align: "right" },
  { header: "Status", cell: (row) => <StatusDot {...activeStatus(row)} />, exportValue: (row) => activeStatus(row).label },
];

export function CurriculaPanel({ rows, subjects, isLoading, isError, onRetry, canWrite, onOpenSubject }: {
  rows?: CurriculumRow[];
  subjects: Subject[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canWrite: boolean;
  onOpenSubject: (subject: Subject) => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [editing, setEditing] = useState<{ record?: Curriculum } | null>(null);
  const [dialog, setDialog] = useState<ConfirmRequest | null>(null);
  const usage = useQuery({
    queryKey: ["academics-record", "curriculum", viewing],
    queryFn: () => curriculumService.getCurriculum(viewing!),
    enabled: viewing !== null && canWrite,
  });
  const actions = useRecordMutations<Partial<Curriculum> & { id?: number }>({
    noun: "Curriculum",
    save: ({ id, ...data }) => (id ? curriculumService.updateCurriculum(id, data) : curriculumService.createCurriculum({ ...data, is_active: true })),
    setActive: (id, isActive) => curriculumService.updateCurriculum(id, { is_active: isActive }),
    remove: curriculumService.deleteCurriculum,
    onSaved: () => setEditing(null),
    onDeleted: () => {
      setDialog(null);
      setViewing(null);
    },
    setNotice,
  });

  const curriculum = viewing !== null && !editing && !dialog ? rows?.find((row) => row.id === viewing) : undefined;
  const own = curriculum ? subjects.filter((subject) => subject.curriculum === curriculum.id) : [];
  const busy = actions.toggle.isPending || actions.remove.isPending;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Curricula"
        description="The programmes the school follows. Every subject belongs to one."
        actions={canWrite && (
          <button type="button" onClick={() => { actions.save.reset(); setEditing({}); }} className="btn-primary h-8 min-h-8 px-3 text-xs">
            <Plus className="mr-1.5 h-3.5 w-3.5" /> New curriculum
          </button>
        )}
        rows={rows}
        config={curriculumConfig}
        columns={curriculumColumns}
        rowKey={(row) => row.id}
        onOpen={(row) => setViewing(row.id)}
        noun={["curriculum", "curricula"]}
        exportName={{ file: "curricula", title: "Curricula" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canWrite ? "No curricula yet. Create the first one, then add its subjects." : "No curricula have been set up yet."}
      />

      {curriculum && (
        <DetailsModal
          title={curriculum.name}
          headline={{
            label: "Subjects",
            value: String(curriculum.subjectCount),
            note: `${levelLabel(curriculum.education_level)} · ${labelFrom(CURRICULUM_TYPES, curriculum.curriculum_type)} · version ${curriculum.version}`,
          }}
          status={activeStatus(curriculum)}
          facts={[
            ["Type", labelFrom(CURRICULUM_TYPES, curriculum.curriculum_type)],
            ["Level of schooling", levelLabel(curriculum.education_level)],
            ["Version", curriculum.version],
            ["Issued by", curriculum.curriculum_authority],
            ["Country", curriculum.country],
            ["Language of teaching", curriculum.language],
            ["In use from", curriculum.start_date],
            ["In use until", curriculum.end_date || "Still in use"],
            ["Required by the authority", curriculum.is_mandatory === false ? "No" : "Yes"],
            ["Syllabi", String(curriculum.syllabusCount)],
          ]}
          notice={<UsageNotice inUse={usage.data?.in_use} advice="Make it inactive to stop new subjects being added to it." />}
          sections={[
            { title: "Description", body: curriculum.description },
            { title: "Objectives", body: curriculum.objectives },
            { title: "Assessment criteria", body: curriculum.assessment_criteria },
            { title: "Resources required", body: curriculum.resources_required },
            { title: "Notes", body: curriculum.notes },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <RecordFooter
              canWrite={canWrite}
              isActive={curriculum.is_active}
              inUse={usage.data?.in_use}
              busy={busy}
              onClose={() => setViewing(null)}
              onEdit={() => { actions.save.reset(); setEditing({ record: curriculum }); }}
              onToggle={() => actions.toggle.mutate({ id: curriculum.id, isActive: !curriculum.is_active })}
              onDelete={() =>
                setDialog({
                  title: "Delete curriculum?",
                  body: <p><strong>{curriculum.name}</strong> will be permanently removed. Nothing uses it yet, and this cannot be undone.</p>,
                  confirmLabel: "Delete curriculum",
                  tone: "danger",
                  onConfirm: () => actions.remove.mutate(curriculum.id),
                })
              }
            />
          }
        >
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Its subjects</h4>
            {own.length ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {own.map((subject) => (
                  <button
                    key={subject.id}
                    type="button"
                    onClick={() => onOpenSubject(subject)}
                    className={`rounded-full border px-2.5 py-1 text-xs hover:border-primary-300 hover:bg-primary-50 ${subject.is_active ? "border-gray-200 text-gray-700" : "border-dashed border-gray-300 text-gray-400"}`}
                  >
                    {subject.name} <span className="font-mono text-gray-400">{subject.code}</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="mt-1.5 text-sm text-gray-500">No subjects yet. Add them from the Subjects tab.</p>
            )}
          </section>
        </DetailsModal>
      )}

      {editing && (
        <CurriculumForm
          record={editing.record}
          busy={actions.save.isPending}
          error={actions.save.isError ? apiErrorMessage(actions.save.error, "The curriculum could not be saved.") : undefined}
          onSave={(data) => actions.save.mutate({ ...data, id: editing.record?.id })}
          onClose={() => setEditing(null)}
        />
      )}
      <ConfirmDialog request={dialog} onClose={() => setDialog(null)} busy={actions.remove.isPending} />
    </div>
  );
}
